import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Tag, BookOpen, Search } from 'lucide-react';
import { blogPosts, blogCategories } from '../data/blogData';
import Navbar from './Navbar';
import Footer from './Footer';

const BlogList = () => {
  const [active, setActive] = useState('All');
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    return blogPosts.filter((p) => {
      const matchesCat = active === 'All' || p.category === active;
      const matchesQuery = !query || p.title.toLowerCase().includes(query.toLowerCase()) || p.excerpt.toLowerCase().includes(query.toLowerCase());
      return matchesCat && matchesQuery;
    });
  }, [active, query]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Navbar />

      {/* Hero */}
      <section className="border-b border-gray-200 bg-white" data-testid="blog-hero">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium mb-4">
            <BookOpen size={14} /> Blog &amp; Resources
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-gray-900 max-w-4xl">
            Expert guides on medical certificates, leave rules and Indian healthcare law
          </h1>
          <p className="mt-5 text-base sm:text-lg text-gray-600 max-w-3xl">
            In-depth, SEO-friendly articles covering medical certificate formats, sick leave documentation, telemedicine validity, government guidelines and responsible use of document tools. All original copy.
          </p>

          {/* Search */}
          <div className="mt-8 max-w-2xl relative">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search articles by title or topic..."
              data-testid="blog-search-input"
              className="w-full pl-11 pr-4 py-3 rounded-full border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition"
            />
          </div>
        </div>
      </section>

      {/* Category chips */}
      <div className="border-b border-gray-200 bg-white sticky top-16 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 overflow-x-auto" data-testid="blog-category-chips">
          <div className="flex items-center gap-2 min-w-max">
            {blogCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActive(cat)}
                data-testid={`blog-cat-${cat.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and')}`}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition ${
                  active === cat
                    ? 'bg-gray-900 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Grid */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <div className="mb-6 text-sm text-gray-500" data-testid="blog-results-count">
          Showing {filtered.length} article{filtered.length === 1 ? '' : 's'}
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-20 text-gray-500" data-testid="blog-empty-state">
            No articles match your search. Try a different keyword or category.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((post) => (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                data-testid={`blog-card-${post.slug}`}
                className="group bg-white border border-gray-200 rounded-2xl p-6 hover:border-blue-400 hover:shadow-lg transition-all"
              >
                <div className="inline-flex items-center gap-2 text-xs font-medium text-blue-700 mb-3">
                  <Tag size={12} /> {post.category}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 leading-snug group-hover:text-blue-700 transition-colors">
                  {post.title}
                </h3>
                <p className="mt-3 text-sm text-gray-600 line-clamp-3">{post.excerpt}</p>
                <div className="mt-5 flex items-center justify-between text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <Clock size={12} /> {post.readTime}
                  </span>
                  <span>{post.updated}</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
};

export default BlogList;
